import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  VStack,
  Heading,
  Text,
  Button,
  useColorModeValue,
  Icon,
  Spinner,
  Progress,
  Card,
  Grid,
  GridItem,
  Container,
  Alert,
  AlertIcon,
  SimpleGrid,
} from "@chakra-ui/react";
import { FaPlusCircle, FaList } from "react-icons/fa";
import { motion } from "framer-motion";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";
import { useQuota } from "../../context/QuotaContext";
import { firestore, doc, getDoc } from "../../firebase/firebaseConfig";
import {
  getTradeCount,
  getActiveListings,
  getActiveSubscription,
} from "../../services/firebaseService";
import { fetchPlanByStripePriceId } from "../../services/planService";
import { fetchGlobalNotifications, fetchUserNotifications, updateNotificationStatus } from "../../services/notificationService";
import AlertNotification from "../../components/AlertNotification";
import { trackUserEngagement } from "../../utils/analytics";
import PaymentRequest from '../../components/PaymentRequest.jsx';
import DashboardLeaderboard from '../../components/DashboardLeaderboard';
import AffiliateDashboard from '../../components/AffiliateDashboard';
import ReferralCodePage from '../../components/ReferralCodePage';
import { useTranslation } from "react-i18next";


const DashboardPage = () => {
  const { t, i18n } = useTranslation();

  const { user } = useAuth();
  const { tradesThisCycle, tradeLimit, activeListings, listingLimit, quotaExceeded, freeTrades } = useQuota();

  const location = useLocation();
  const [userName, setUserName] = useState("User");
  const [loading, setLoading] = useState(true);
  const [tradesUsed, setTradesUsed] = useState(0);
  //const [activeListings, setActiveListings] = useState(0);
  //const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [planDetails, setPlanDetails] = useState({});
  const [notifications, setNotifications] = useState([]);
  //const [freeTrades, setFreeTrades] = useState(0);
  const [userSubscribed, setUserSubscribed] = useState(false);
  //const [userTradeLimit, setUserTradeLimit] = useState("N/A");

  // Theme Colors
  const bgColor = useColorModeValue("brand.200", "gray.900");
  const cardBgColor = useColorModeValue("brand.100", "gray.700");
  const textColor = useColorModeValue("brand.300", "white");


  /**
   * Fetch Dashboard Data
   */
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.uid) {
        console.warn("No authenticated user detected.");
        setLoading(false);
        return;
      }

      try {
        console.log("Fetching dashboard data for user:", user.uid);

        // Fetch user details
        const userDocRef = doc(firestore, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          console.log("User Data:", userData);
          setUserName(userData.userName || "User");
          //console.log(`🔥 Retrieved Free Trades from Firestore: ${userData.freeTrades}`);
          //setFreeTrades(userData.freeTrades ?? 0); // ✅ Ensure Firestore updates are applied
        } else {
          console.warn("User document not found.");
        }

        // Fetch the user's active subscription
        const activeSubscription = await getActiveSubscription(user.uid);
        if (activeSubscription?.planId) {
          setUserSubscribed(true);

          const planData = await fetchPlanByStripePriceId(activeSubscription.planId);
          const matchingCycle = planData.billingCycles.find(
            (cycle) => cycle.stripePriceId === activeSubscription.planId
          );

          setPlanDetails({
            tradeLimit: matchingCycle?.tradeLimit ?? "N/A",
            listingLimit: matchingCycle?.listingLimit ?? "N/A",
          });
          //setUserTradeLimit(matchingCycle?.tradeLimit ?? "N/A");

        } else {
          setUserSubscribed(false);
          setPlanDetails({ tradeLimit: "N/A", listingLimit: "N/A" });
          //setUserTradeLimit("N/A"); // ✅ User has no subscription, set default trade limit
        }

        // Fetch usage data
        const tradesUsed = await getTradeCount(user.uid);
        //const activeListings = await getActiveListings(user.uid);
        setTradesUsed(tradesUsed);
        //setActiveListings(activeListings);

        // Check if the user has exceeded their quota
        /* if ((userSubscribed && userTradeLimit !== "N/A" && tradesUsed >= userTradeLimit) || activeListings >= 1) {
          setQuotaExceeded(true);
        } */
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();

    //console.log(`✅ Free Trades Updated: ${freeTrades}`);
  }, [user]); // ✅ Now updates whenever `freeTrades` changes

  useEffect(() => {
    const loadNotifications = async () => {

      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Fetch global and user-specific notifications
        const [globalAlerts, userAlerts] = await Promise.all([
          fetchGlobalNotifications(),
          user ? fetchUserNotifications(user.uid) : []
        ]);

        // Filter out dismissed notifications from both sources
        const activeGlobalAlerts = globalAlerts.filter((notif) => notif.status !== "dismissed");
        const activeUserAlerts = userAlerts.filter((notif) => notif.status !== "dismissed");

        // Merge global and user notifications & sort by createdAt
        const allNotifications = [...activeGlobalAlerts, ...activeUserAlerts].sort(
          (a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0)
        );

        setNotifications(allNotifications);

        // Mark unread notifications as read
        allNotifications.forEach(async (notif) => {
          if (notif.status === "unread") {
            await updateNotificationStatus(user.uid, notif.id, "read");
          }
        });

      } catch (error) {
        console.error("Error loading notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, [user]);

  const handleDismissNotification = async (id) => {
    await updateNotificationStatus(user.uid, id, "dismissed");

    // Remove notification from UI
    setNotifications((prevNotifications) => prevNotifications.filter((notif) => notif.id !== id));
  };

  useEffect(() => {
    trackUserEngagement('dashboard_viewed', { timestamp: Date.now() });
  }, []);

  // Reusable Feature Card Component
  const FeatureCard = ({ title, description, content }) => {
    const cardBgColor = useColorModeValue("brand.100", "gray.700");
    const textColor = useColorModeValue("brand.300", "white");

    return (
      <Card bg={cardBgColor} color={textColor} p={6} borderRadius="lg" shadow="lg" transition="all 0.3s ease" _hover={{ transform: "scale(1.05)", shadow: "xl" }} h="100%">
        <Heading size="lg" mb={3}>
          {title}
        </Heading>
        <Text fontSize="sm" mb={4} color="brand.300">
          {description}
        </Text>
        {content} {/* Render the component passed as content */}
      </Card>
    );
  };

  if (loading) {
    return (
      <Flex height="100vh" align="center" justify="center">
        <Spinner size="xl" />
        <Text ml={4} color="gray.500">
          {t("dashboardpage.loading_dashboard...")}
        </Text>
      </Flex>
    );
  }

  return (
    <Box bg={bgColor} color="brand.200" background='brand.200'>
      <Container maxW="container.xl" py={8}>
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(6, 1fr)" }}
          templateRows="auto"
          gap={6}
        >

          {/* Welcome Card */}
          <GridItem colSpan={{ base: 6, md: 6 }} textAlign="center">
            <Card bg={cardBgColor} p={6} borderRadius="lg" shadow="md">
              <Heading 
                as={motion.h1}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1 }}
                size="2xl"
                fontWeight="bold"
                mb={4}
                color="brand.300"
              >
                {t("dashboardpage.welcome")}, {userName}!
              </Heading>
              <Text 
                as={motion.p}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                fontSize="xl" 
                mb={6}
                color="brand.400"
              >
                {t("dashboardpage.manage_your_listings_and_explore_deals_easily_from_your_dashboard.")}
              </Text>
            </Card>
          </GridItem>

          {/* Subscription and Quota Card */}
          <GridItem colSpan={{ base: 6, md: 3 }}>
            <Card bg={cardBgColor} p={6} borderRadius="lg" shadow="md" h="100%">
              <VStack spacing={6} align="stretch">
                <Heading size="lg" fontWeight="bold" color={textColor}>
                  {t("dashboardpage.subscription_status")}
                </Heading>

                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Free Trades: {freeTrades}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  Total Trades: {tradesUsed}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                {t("dashboardpage.active_listings")}: {activeListings} / {listingLimit === "N/A" ? t("dashboardpage.Unlimited") : listingLimit}
                </Text>
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  {t("dashboardpage.trade_quota")}:{" "}
                  {tradeLimit === "N/A" ? "N/A" : tradeLimit === -1 ? t("dashboardpage.Unlimited") : `${tradesThisCycle} / ${tradeLimit}`}
                </Text>

                <Progress
                  value={
                    userSubscribed && planDetails?.tradeLimit && planDetails?.tradeLimit > 0
                      ? (tradesThisCycle / planDetails.tradeLimit) * 100
                      : 0
                  }
                  colorScheme="blue"
                  mt={2}
                />
              </VStack>
            </Card>
          </GridItem>

          {/* Action Buttons */}
          <GridItem colSpan={{ base: 6, md: 3 }}>
            <Card bg={cardBgColor} p={6} borderRadius="lg" shadow="md" h="100%">
              <VStack spacing={6} align="stretch">
                <Flex justify="center" gap={6} wrap="wrap">
                  <Box
                    bg={cardBgColor}
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    maxW={{ base: "100%", md: "600px" }}
                  >
                    <Heading size="md" color={textColor} mb={4}>
                      {t("dashboardpage.find_a_deal")}
                    </Heading>
                    <Text mb={6} color="brand.300">
                      {t("dashboardpage.browse_listings_to_find_deals_that_suit_your_needs_and_contact_sellers_directly.")}
                    </Text>
                    <Button
                      leftIcon={<Icon as={FaList} />}
                      as={RouterLink}
                      to="/personal/listings"
                      colorScheme="brand.100"
                      _hover={{ bg: "brand.100", color: "brand.300", transform: "scale(1.05)" }}
                      color="white"
                      bg="brand.300"
                      size="lg"
                      shadow="xl"
                      w="100%"
                    >
                      {t("dashboardpage.view_listings")}
                    </Button>
                  </Box>

                  <Box
                    bg={cardBgColor}
                    p={6}
                    borderRadius="lg"
                    textAlign="center"
                    maxW={{ base: "100%", md: "600px" }}
                  >
                    <Heading size="md" color={textColor} mb={4}>
                      {t("dashboardpage.post_your_ad")}
                    </Heading>
                    <Text mb={6} color="brand.300">
                      {t("dashboardpage.looking _to_send_or_exchange?_post_your_ad_and_let_others_find_you.")}
                    </Text>
                    <Button
                      leftIcon={<Icon as={FaPlusCircle} />}
                      as={RouterLink}
                      to="/personal/post-listing"
                      colorScheme="brand.300"
                      size="lg"
                      shadow="xl"
                      isDisabled={quotaExceeded || activeListings >= planDetails?.listingLimit}
                      w="100%"
                    >
                      {t("dashboardpage.create_listing")}
                    </Button>
                  </Box>
                </Flex>

                {/* Quota Notifications */}
                {quotaExceeded && (
                  <Text color="red.500" fontSize="md" mt={2}>
                    {t("dashboardpage.you_have_reached_your_trade_quota._upgrade_your_plan_to_continue.")}
                  </Text>
                )}
                {activeListings >= planDetails?.listingLimit && (
                  <Text color="yellow.500" fontSize="sm" mt={4}>
                    {t("dashboardpage.maximum_active_listings_reached._deactivate_a_listing_to_create_a_new_one.")}
                  </Text>
                )}
              </VStack>
            </Card>
          </GridItem>

          {/* Alerts & Notifications Section */}
          <GridItem colSpan={6}>
            <Card bg={cardBgColor} p={6} borderRadius="lg" shadow="md">
              <Heading size="lg" color={textColor} mb={4}>
                {t("dashboardpage.alerts_&_notifications")}
              </Heading>
              {notifications.length === 0 ? (
                <Text color="brand.300">{t("dashboardpage.you're_all_caught_up._no_new_notifications.")}</Text>
              ) : (
                <VStack spacing={4}>
                  {notifications.map((notification) => (
                    <AlertNotification
                      key={notification.id}
                      {...notification}
                      onMarkAsRead={async (id) => {
                        await updateNotificationStatus(user.uid, id, "read");
                      }}
                      onDismiss={handleDismissNotification}
                    />
                  ))}
                </VStack>
              )}
            </Card>
          </GridItem>

          {/* Feature Grid - Replaced SimpleGrid */}
          <GridItem colSpan={{ base: 6, md: 6, lg: 6 }}> {/* Occupy full width on all screens */}
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={6}>
              {/* <GridItem> {/* Payment Requests 
                <FeatureCard
                  title={t("dashboardpage.payment_requests")}
                  description={t("dashboardpage.manage_pending_payment_requests_here.")}
                  content={<PaymentRequest tradeId="sampleTrade" sellerId={user.uid} />}
                />              
              </GridItem> */}
              <GridItem colSpan={{ base: 6, md: 2, lg: 2 }}> {/* Leaderboard */}
                <FeatureCard
                  title={t("dashboardpage.leaderboard")}
                  description={t("dashboardpage.check_out_the_top_performers.")}
                  content={<DashboardLeaderboard />}
                />
              </GridItem>
              <GridItem colSpan={{ base: 6, md: 1, lg: 1 }}> {/* Affiliate Dashboard */}
                <FeatureCard
                  title={t("dashboardpage.affiliate_dashboard")}
                  description={t("dashboardpage.track_your_referrals_and_commissions.")}
                  content={<AffiliateDashboard userId={user.uid} />}
                />
              </GridItem>
              {/*               <GridItem> {/* Referral Code *
                <FeatureCard
                  title="Referral Code"
                  description="Get your referral code here."
                  content={<ReferralCodePage />}
                />              
              </GridItem> */}
            </Grid>
          </GridItem>


        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardPage;
